import { useCallback, useEffect, useRef, useState } from 'react';
import MapGL, { Layer, Popup, Source, MapRef } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Badge, Box, Flex, Text, useColorModeValue } from '@chakra-ui/react';
import Card from 'components/card/Card';
import { sectionLayer, sectionLabelLayer } from './layers';

const MAPBOX_TOKEN = 'pk.eyJ1Ijoic2ltbW1wbGUiLCJhIjoiY2wxeG1hd24xMDEzYzNrbWs5emFkdm16ZiJ9.q9s0sSKQFFaT9fyrC-7--g';
const REFRESH_MS = 15000;

interface PopupData {
  lngLat: [number, number];
  seccion: string;
  municipio: string;
  confirmados: number;
  promovidos: number;
  negativos: number;
  total: number;
  porcentaje: number;
}

export default function MapCard(props: { [x: string]: any }) {
  const { ...rest } = props;
  const mapRef = useRef<MapRef>(null);
  const [geoData, setGeoData] = useState<any>(null);
  const [popupInfo, setPopupInfo] = useState<PopupData | null>(null);
  const [lastUpdate, setLastUpdate] = useState<string>('');
  const [mapLoaded, setMapLoaded] = useState(false);

  const mapStyles = useColorModeValue(
    'mapbox://styles/simmmple/ckwxecg1wapzp14s9qlus38p0',
    'mapbox://styles/simmmple/cl0qqjr3z000814pq7428ptk5'
  );

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(process.env.REACT_APP_URL_SERVER + '/mapeo', { credentials: 'include' });
      const data = await res.json();
      setGeoData(data);
      setLastUpdate(new Date().toLocaleTimeString('es-MX'));
    } catch (e) {
      console.error('Error cargando mapa:', e);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, REFRESH_MS);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    if (!mapLoaded || !geoData?.features?.length || !mapRef.current) return;

    const coords = geoData.features
      .map((feature: any) => feature?.geometry?.coordinates)
      .filter((coordinates: any) => Array.isArray(coordinates) && coordinates.length === 2);

    if (!coords.length) return;

    const bounds = coords.reduce((acc: any, [lng, lat]: [number, number]) => {
      return [
        [Math.min(acc[0][0], lng), Math.min(acc[0][1], lat)],
        [Math.max(acc[1][0], lng), Math.max(acc[1][1], lat)],
      ];
    }, [
      [coords[0][0], coords[0][1]],
      [coords[0][0], coords[0][1]],
    ]);

    mapRef.current.fitBounds(bounds as [[number, number], [number, number]], {
      padding: 60,
      duration: 700,
      maxZoom: 13,
    });
  }, [geoData, mapLoaded]);

  const onClick = (e: any) => {
    const features = mapRef.current?.queryRenderedFeatures(e.point, { layers: ['sections'] });
    if (features && features.length > 0) {
      const f = features[0];
      const geom = JSON.parse(JSON.stringify(f.geometry));
      const p = f.properties as any;
      setPopupInfo({
        lngLat: geom.coordinates,
        seccion: p.seccion,
        municipio: p.municipio || '',
        confirmados: p.confirmados,
        promovidos: p.promovidos,
        negativos: p.negativos,
        total: p.total,
        porcentaje: p.porcentaje,
      });
    }
  };

  const textColor = useColorModeValue('secondaryGray.900', 'white');

  return (
    <Card w="100%" h="100%" display="flex" flexDirection="column" pb="0px" overflow="hidden" {...rest}>
      <Flex justify="space-between" align="center" px="20px" pt="16px" pb="12px" flexWrap="wrap" gap="10px">
        <Flex direction="column">
          <Text color={textColor} fontSize="18px" fontWeight="700" lineHeight="100%">
            Mapa Electoral en Tiempo Real
          </Text>
          {lastUpdate && (
            <Text color="secondaryGray.600" fontSize="xs" mt="4px">
              Actualizado: {lastUpdate} · refresca cada {REFRESH_MS / 1000}s
            </Text>
          )}
        </Flex>
        <Flex gap="8px" align="center" flexWrap="wrap">
          <Badge px="8px" py="4px" borderRadius="8px" bg="green.400" color="white" fontSize="xs">Confirmado</Badge>
          <Badge px="8px" py="4px" borderRadius="8px" bg="yellow.400" color="white" fontSize="xs">Promovido</Badge>
          <Badge px="8px" py="4px" borderRadius="8px" bg="red.400" color="white" fontSize="xs">Sin confirmar</Badge>
          <Badge px="8px" py="4px" borderRadius="8px" bg="gray.500" color="white" fontSize="xs">Tamaño = total</Badge>
        </Flex>
      </Flex>

      <Box w="100%" flex="1" minH={{ base: '420px', xl: '760px' }}>
        <MapGL
          initialViewState={{
            longitude: -98.2375,
            latitude: 19.3183,
            zoom: 9.5
          }}
          style={{ width: '100%', height: '100%', minHeight: '420px' }}
          mapStyle={mapStyles}
          mapboxAccessToken={MAPBOX_TOKEN}
          ref={mapRef}
          interactiveLayerIds={['sections']}
          onClick={onClick}
          onLoad={() => setMapLoaded(true)}
        >
          {geoData && (
            <Source id="informacion" type="geojson" data={geoData}>
              <Layer {...sectionLayer} />
              <Layer {...sectionLabelLayer} />
            </Source>
          )}

          {popupInfo && (
            <Popup
              longitude={popupInfo.lngLat[0]}
              latitude={popupInfo.lngLat[1]}
              onClose={() => setPopupInfo(null)}
              closeOnClick={false}
              style={{ padding: 0 }}
            >
              <Box p="10px 14px" minW="170px" fontSize="13px" lineHeight="1.8">
                <Text fontWeight="700" mb="2px">{popupInfo.municipio}</Text>
                <Text fontWeight="600" mb="4px" color="gray.600">Sección {popupInfo.seccion}</Text>
                <Text>Confirmados: <b>{popupInfo.confirmados}</b></Text>
                <Text>Promovidos: <b>{popupInfo.promovidos}</b></Text>
                <Text>Sin confirmar: <b>{popupInfo.negativos}</b></Text>
                <Text borderTop="1px solid #eee" mt="6px" pt="4px">
                  Total: <b>{popupInfo.total}</b> · <b>{popupInfo.porcentaje}%</b> confirmación
                </Text>
              </Box>
            </Popup>
          )}
        </MapGL>
      </Box>
    </Card>
  );
}
